import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/system';

import { AccordionPayment } from 'components/AccordionPayment/AccordionPayment';
import { FinancialStats } from 'components/FinancialStats/FinancialStats';
import { FinanceCharts } from 'components/FinanceCharts/FinanceCharts';
import { ListExpenses } from 'components/ListExpenses/ListExpenses';
import { BorderLinearProgress } from 'components/Loader/Loader';
import { FinanceFilters } from 'components/MainFilters/FinanceFilters';
import { FinanceNavigation } from 'components/Navigation/FinanceNavigation';

import { useGetCompanies } from 'hooks/companies/useGetCompanies';
import { useGetServicePeriods } from 'hooks/periods/useGetServicePeriods';
import { useGetMainFinanceStats } from 'hooks/finance/useGetMainFinanceStats';
import { useGetPayments } from 'hooks/finance/useGetPayments';
import { useGetListExpenses } from 'hooks/finance/useGetListExpenses';

import { monthsData } from 'utils/constants';
import { sessionStorageGetItem } from 'utils/sessionStorageGetItem';
import { throttle } from 'utils/tablesMethods';

import {
  IDataFinancePeriodsStats,
  IDataFinanceStats,
  IFinanceProps,
  IListExpenses,
} from 'interfaces/finance/financeInterfaces';
import { Company, DataPeriod, ErrorData } from 'interfaces/global/globalInterfaces';
import { isAccessAllowed } from 'utils/isAccessAllowed';
import { InfoText } from 'ui/other/Typography';
import { useGetCompanyAdmins } from 'hooks/permissions/useGetCompanyAdmins';
import { useGetAccessList } from 'hooks/permissions/useGetAccessList';
import { useGetDefaultPermissions } from 'hooks/permissions/useGetDefaultPermissions';
import { useSnackbar } from 'notistack';
import { AdditionalServicesModal } from 'shared/modals/AdditionalServicesModa/AdditionalServicesModal';

interface ISelectedFilter {
  id: string;
  title: string;
}

const Finance: React.FC<IFinanceProps> = ({ handleManagersHR }) => {
  // sessionStorage для главных фильтров
  const mainFilterCompanyFinance = sessionStorageGetItem<Company>('mainFilterCompanyFinance');
  const mainFilterPeriodFinance = sessionStorageGetItem<DataPeriod>('mainFilterPeriodFinance');

  // sessionStorage для фильтров таблицы
  const tableCheckFinance = sessionStorageGetItem<boolean>('tableCheckFinance', false);
  const tableFilterFinance = sessionStorageGetItem<string>('tableFilterFinance');

  const [selectedCompanyByFinance, setSelectedCompanyByFinance] = useState<Company | null>(
    mainFilterCompanyFinance || null
  );
  const [selectedPeriodByFinance, setSelectedPeriodByFinance] = useState<DataPeriod | null>(
    mainFilterPeriodFinance || null
  );
  const [isCheckedListExpenses, setCheckedListExpenses] = useState<boolean>(tableCheckFinance || false);
  const [selectedFilterByTitle, setSelectedFilterByTitle] = useState<string | null>(tableFilterFinance || null);
  const [isAdditionalServicesModal, setAdditionalServicesModal] = useState<boolean>(false);

  const token = localStorage.getItem('token');

  const { enqueueSnackbar } = useSnackbar();

  const { accessList } = useGetAccessList({
    isLoggedIn: !!token,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { defaultPermissions } = useGetDefaultPermissions({
    isLoggedIn: !!token,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const { isLoadingCompanies, companiesSearch: allCompanies } = useGetCompanies();

  useEffect(() => {
    if (!allCompanies.length) {
      return;
    }

    const userClearedCompany = sessionStorage.getItem('mainFilterCompanyFinanceCleared') === 'true';
    const storedCompany = sessionStorageGetItem<Company>('mainFilterCompanyFinance');

    if (!storedCompany?.uuid && !userClearedCompany) {
      setSelectedCompanyByFinance(allCompanies[0]);
      sessionStorage.setItem('mainFilterCompanyFinance', JSON.stringify(allCompanies[0]));
      return;
    }

    if (storedCompany?.uuid) {
      const matchedCompany = allCompanies.find((company) => company.uuid === storedCompany.uuid) ?? null;
      if (matchedCompany) {
        setSelectedCompanyByFinance(matchedCompany);
        sessionStorage.setItem('mainFilterCompanyFinance', JSON.stringify(matchedCompany));
      }
    }
  }, [allCompanies]);

  const { isLoadingServicePeriods, dataServicePeriods } = useGetServicePeriods({
    companies: selectedCompanyByFinance ? [selectedCompanyByFinance] : [],
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const dataPeriods = useMemo(
    () => (dataServicePeriods ? [...dataServicePeriods].reverse() : []),
    [dataServicePeriods]
  );

  useEffect(() => {
    if (!selectedCompanyByFinance) {
      setSelectedPeriodByFinance(null);
      sessionStorage.setItem('mainFilterPeriodFinance', JSON.stringify(''));
      return;
    }

    if (!dataServicePeriods) {
      return;
    }

    const storedPeriod = sessionStorageGetItem<DataPeriod>('mainFilterPeriodFinance');
    const activePeriodExist = dataPeriods.some((item) => item.activePeriod);

    dataPeriods.forEach((period: DataPeriod) => {
      if (storedPeriod) {
        if (storedPeriod.uuid === period.uuid) {
          setSelectedPeriodByFinance(period);
          sessionStorage.setItem('mainFilterPeriodFinance', JSON.stringify(period));
          handleManagersHR({
            accountEmail: period.accountEmail,
            accountPhone: period.accountPhone,
            curatorEmail: period.curatorEmail,
            curatorPhone: period.curatorPhone,
            accountFullName: period.accountFullName,
            doctorFullName: period.doctorFullName,
            doctorEmail: period.doctorEmail,
          });
        }
      } else if (period.activePeriod) {
        setSelectedPeriodByFinance(period);
        sessionStorage.setItem('mainFilterPeriodFinance', JSON.stringify(period));

        handleManagersHR({
          accountEmail: period.accountEmail,
          accountPhone: period.accountPhone,
          curatorEmail: period.curatorEmail,
          curatorPhone: period.curatorPhone,
          accountFullName: period.accountFullName,
          doctorFullName: period.doctorFullName,
        });
      } else if (!activePeriodExist) {
        setSelectedPeriodByFinance(dataPeriods[0]);
        sessionStorage.setItem('mainFilterPeriodFinance', JSON.stringify(dataPeriods[0]));

        handleManagersHR({
          accountEmail: dataPeriods[0].accountEmail,
          accountPhone: dataPeriods[0].accountPhone,
          curatorEmail: dataPeriods[0].curatorEmail,
          curatorPhone: dataPeriods[0].curatorPhone,
          accountFullName: dataPeriods[0].accountFullName,
          doctorFullName: dataPeriods[0].doctorFullName,
        });
      }
    });
  }, [dataPeriods, dataServicePeriods, handleManagersHR, selectedCompanyByFinance]);

  const { isLoadingMainFinanceStats, isErrorMainFinanceStats, newDataFinanceStats } = useGetMainFinanceStats({
    isStatsAllowed:
      isAccessAllowed(mainFilterCompanyFinance?.uuid, 'HR_finance_stats', accessList, defaultPermissions) &&
      isAccessAllowed(mainFilterCompanyFinance?.uuid, 'HR_finance', accessList, defaultPermissions) &&
      selectedPeriodByFinance?.contractType === 'fronting',
    servicePeriod: selectedPeriodByFinance,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const dataFinanceStats: IDataFinanceStats = useMemo(() => {
    if (!newDataFinanceStats) {
      return {
        moneyTotal: null,
        deposite: null,
        budget: null,
        moneyFranchise: null,
      };
    }

    const dataMoneyTotal = Number(newDataFinanceStats.money_total);
    const dataDeposite = Number(newDataFinanceStats.deposite);
    const moneyFranchise: number = dataDeposite - dataMoneyTotal;

    return {
      moneyTotal: newDataFinanceStats.money_total || null,
      deposite: newDataFinanceStats.deposite || null,
      budget: newDataFinanceStats.budget || null,
      moneyFranchise: moneyFranchise.toFixed(2),
    };
  }, [newDataFinanceStats]);

  const dataFinancePeriodsStats: IDataFinancePeriodsStats[] = useMemo(() => {
    if (!newDataFinanceStats?.per_month_stat) {
      return [];
    }

    const periodsMonthStats: IDataFinancePeriodsStats[] = newDataFinanceStats.per_month_stat.map(
      (el: { money_total: string; month: string; num_visits: number }) => {
        const monthString = el.month.substr(-2, 4);
        const yearString = el.month.substr(2, 2);
        return {
          money: Number(el.money_total),
          month: `${monthsData[monthString]} '${yearString}`,
          visits: el.num_visits,
        };
      }
    );
    periodsMonthStats.push({
      money: Number(newDataFinanceStats.expenditures_after_period.money_total),
      month: 'Доп.',
      visits: newDataFinanceStats.expenditures_after_period.num_visits,
    });
    return periodsMonthStats;
  }, [newDataFinanceStats]);

  const {
    isLoadingPayments,
    isErrorPayments,
    hasNextPagePayments,
    isFetchingNextPagePayments,
    fetchNextPage,
    dataPayments = [],
  } = useGetPayments({
    isPaymentsAllowed: isAccessAllowed(mainFilterCompanyFinance?.uuid, 'HR_finance', accessList, defaultPermissions),
    company: selectedCompanyByFinance || null,
    servicePeriod: selectedPeriodByFinance || null,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const {
    isLoadingListExpenses,
    fetchNextPageListEXpenses,
    hasNextPageListEXpenses,
    isFetchingNextPageListEXpenses,
    isFetchingListExpenses,
    dataListExpenses,
  } = useGetListExpenses({
    isListAllowed:
      isAccessAllowed(mainFilterCompanyFinance?.uuid, 'HR_finance_reports', accessList, defaultPermissions) &&
      isAccessAllowed(mainFilterCompanyFinance?.uuid, 'HR_finance', accessList, defaultPermissions) &&
      selectedPeriodByFinance?.contractType === 'fronting',
    listExpenses: isCheckedListExpenses,
    company: selectedCompanyByFinance || null,
    servicePeriod: selectedPeriodByFinance || null,
    selectedFilterByTitle,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response.json().then((result) => {
          result.errors.forEach((item: ErrorData) =>
            enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
          );
        });
      }
    },
  });

  const optionsFilterByTitle: ISelectedFilter[] = useMemo(() => {
    const loadFilterByTitle = dataListExpenses.map((item: IListExpenses) => ({
      id: item.uuid,
      title: item.title,
    }));

    return loadFilterByTitle
      .filter((a, i) => loadFilterByTitle.findIndex((s) => a.title === s.title && a.title !== '') === i)
      .sort((a, b) => {
        if (a.title > b.title) {
          return 1;
        }
        if (a.title < b.title) {
          return -1;
        }
        return 0;
      });
  }, [dataListExpenses]);

  const { companyAdminsData } = useGetCompanyAdmins({
    companyUuid: selectedCompanyByFinance?.uuid,
    onError: (res) => {
      if (res.response.status >= 400 && res.response.status < 500) {
        res.response
          .json()
          .then((result) =>
            result.errors.forEach((item: ErrorData) =>
              enqueueSnackbar(item.message || 'Произошла ошибка', { variant: 'error' })
            )
          );
      }
    },
  });

  const handleScroll = async (evt: Event): Promise<void> => {
    const { scrollHeight, scrollTop } = (evt.target as Document).documentElement;
    const windowHeight = window.innerHeight;

    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    if (scrollHeight - (windowHeight + scrollTop) < 50) {
      await fetchNextPageListEXpenses();
    }
  };

  useEffect(() => {
    if (hasNextPageListEXpenses && !isFetchingNextPageListEXpenses) {
      document.addEventListener('scroll', throttle(handleScroll, 1000));
    }

    return () => document.removeEventListener('scroll', throttle(handleScroll, 1000));
  }, [hasNextPageListEXpenses, isFetchingNextPageListEXpenses]);

  return (
    <Box>
      {(isLoadingCompanies || isLoadingServicePeriods) && <BorderLinearProgress />}

      <FinanceNavigation
        isFronting={selectedPeriodByFinance?.contractType === 'fronting'}
        selectedPeriodByFinance={selectedPeriodByFinance}
        accessList={accessList}
        defaultPermissions={defaultPermissions}
      />

      <FinanceFilters
        allCompanies={allCompanies}
        dataPeriods={dataPeriods}
        selectedCompanyByFinance={selectedCompanyByFinance}
        handleSelectedCompanyByFinance={setSelectedCompanyByFinance}
        selectedPeriodByFinance={selectedPeriodByFinance}
        handleSelectedPeriodByFinance={setSelectedPeriodByFinance}
      />
      {isAccessAllowed(mainFilterCompanyFinance?.uuid, 'HR_finance', accessList, defaultPermissions) ? (
        <>
          <AccordionPayment
            isLoadingPayments={isLoadingPayments}
            isErrorPayments={!!isErrorPayments}
            hasNextPagePayments={hasNextPagePayments}
            isFetchingNextPagePayments={isFetchingNextPagePayments}
            dataPayments={dataPayments}
            fetchNextPage={fetchNextPage}
          />
          <Box component="section" sx={{ marginTop: '32px' }}>
            {selectedPeriodByFinance?.contractType === 'fronting' && (
              <>
                {isAccessAllowed(
                  mainFilterCompanyFinance?.uuid,
                  'HR_finance_stats',
                  accessList,
                  defaultPermissions
                ) && (
                  <>
                    <FinancialStats
                      dataFinanceStats={dataFinanceStats}
                      isLoadingMainFinanceStats={isLoadingMainFinanceStats}
                      isErrorMainFinanceStats={isErrorMainFinanceStats}
                    />
                    <AdditionalServicesModal
                      open={isAdditionalServicesModal}
                      handleClose={() => setAdditionalServicesModal(false)}
                    />
                  </>
                )}
                <FinanceCharts dataFinancePeriodsStats={dataFinancePeriodsStats} />
                {isAccessAllowed(
                  mainFilterCompanyFinance?.uuid,
                  'HR_finance_reports',
                  accessList,
                  defaultPermissions
                ) && (
                  <ListExpenses
                    isCheckedListExpenses={isCheckedListExpenses}
                    isLoadingListExpenses={isLoadingListExpenses}
                    dataListExpenses={dataListExpenses}
                    handleCheckedListExpenses={setCheckedListExpenses}
                    optionsFilterByTitle={optionsFilterByTitle}
                    selectedFilterByTitle={selectedFilterByTitle}
                    handleSelectedFilterByTitle={setSelectedFilterByTitle}
                    isFetchingListExpenses={isFetchingListExpenses}
                  />
                )}
              </>
            )}
          </Box>
        </>
      ) : (
        <InfoText variant="h5">
          Для просмотра данного раздела у вас недостаточно прав, обратитесь к администратору{' '}
          {companyAdminsData
            ?.slice(0, 3)
            ?.map((item) => item?.user?.email)
            ?.join(',')}
          , если вам необходимы данные для работы
        </InfoText>
      )}
    </Box>
  );
};
export { Finance };
